import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  EVENTO_ABRIR_PREFERENCIAS,
  lerConsentimento,
  salvarConsentimento,
} from '@/lib/consentimento';

/**
 * Banner de consentimento de cookies (LGPD), montado no Layout raiz — aparece
 * em todas as páginas até haver escolha, e reabre pelo "Preferências de
 * cookies" do rodapé.
 *
 * Ele só registra a escolha. Quem efetivamente bloqueia as tags é o Consent
 * Mode (padrão negado no <head> do index.html) somado às verificações de
 * consentimento de cada tag no GTM. As três categorias daqui são as mesmas da
 * /politica-de-cookies: mudou uma, mude a outra.
 *
 * Compacto de propósito: a versão de antes de 26/09/2026 ocupava 40% da tela
 * do celular e cobria o CTA do hero. Na visão inicial o texto e os botões
 * cabem em pouco espaço; o detalhamento só abre em "Configurar".
 */
const CookieConsent = () => {
  const [visivel, setVisivel] = useState(false);
  const [configurando, setConfigurando] = useState(false);
  const [analise, setAnalise] = useState(false);
  const [publicidade, setPublicidade] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!lerConsentimento()) {
      // Espera a página assentar antes de aparecer (não disputa o LCP).
      timer = setTimeout(() => setVisivel(true), 1000);
    }

    const abrir = () => {
      const atual = lerConsentimento();
      setAnalise(atual?.analise ?? false);
      setPublicidade(atual?.publicidade ?? false);
      setConfigurando(true);
      setVisivel(true);
    };
    window.addEventListener(EVENTO_ABRIR_PREFERENCIAS, abrir);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(EVENTO_ABRIR_PREFERENCIAS, abrir);
    };
  }, []);

  const decidir = (escolha: { analise: boolean; publicidade: boolean }) => {
    setVisivel(false);
    setConfigurando(false);
    salvarConsentimento(escolha);
  };

  if (!visivel) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-2 md:p-6 animate-fade-in"
      role="dialog"
      aria-label="Preferências de cookies"
    >
      <div className="container-mobile">
        <div className="bg-card border border-border rounded-xl md:rounded-2xl shadow-lg p-3 md:p-5 max-w-4xl mx-auto">
          <p className="text-xs md:text-sm text-muted-foreground leading-snug">
            Usamos cookies essenciais para o site funcionar e, com a sua permissão, cookies de análise e de
            publicidade do Google e do Meta para entender como o site é usado e medir nossos anúncios. Você pode
            mudar a escolha a qualquer momento.{' '}
            <a href="/politica-de-privacidade" className="text-primary underline underline-offset-2">
              Política de Privacidade
            </a>
          </p>

          {configurando && (
            <div className="mt-3 space-y-3 border-t border-border pt-3">
              <Categoria
                id="cookies-essenciais"
                titulo="Essenciais"
                descricao="Fazem o site funcionar e lembram esta escolha. Sempre ativos."
                marcado
                desabilitado
              />
              <Categoria
                id="cookies-analise"
                titulo="Análise"
                descricao="Google Analytics: quais páginas são vistas e como o site é usado."
                marcado={analise}
                aoMudar={setAnalise}
              />
              <Categoria
                id="cookies-publicidade"
                titulo="Publicidade"
                descricao="Google Ads e Meta (Facebook e Instagram): medir o resultado dos nossos anúncios."
                marcado={publicidade}
                aoMudar={setPublicidade}
              />
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2 md:justify-end">
            {configurando ? (
              <Button
                size="sm"
                onClick={() => decidir({ analise, publicidade })}
                className="flex-1 md:flex-none text-xs md:text-sm"
              >
                Salvar escolha
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfigurando(true)}
                className="flex-1 md:flex-none text-xs md:text-sm"
              >
                Configurar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => decidir({ analise: false, publicidade: false })}
              className="flex-1 md:flex-none text-xs md:text-sm"
            >
              Somente essenciais
            </Button>
            <Button
              size="sm"
              onClick={() => decidir({ analise: true, publicidade: true })}
              className="flex-1 md:flex-none bg-primary hover:bg-primary/90 text-xs md:text-sm"
            >
              Aceitar todos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Categoria = ({
  id,
  titulo,
  descricao,
  marcado,
  desabilitado,
  aoMudar,
}: {
  id: string;
  titulo: string;
  descricao: string;
  marcado: boolean;
  desabilitado?: boolean;
  aoMudar?: (v: boolean) => void;
}) => (
  <div className="flex items-start justify-between gap-3">
    <label htmlFor={id} className="text-xs md:text-sm">
      <span className="font-semibold text-foreground">{titulo}</span>
      <span className="block text-muted-foreground">{descricao}</span>
    </label>
    <Switch id={id} checked={marcado} disabled={desabilitado} onCheckedChange={aoMudar} />
  </div>
);

export default CookieConsent;
